import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./Locations.css";

import mapImg from "../../../../assets/images/about/locations/iq 1.svg";
import { resolveMediaUrl } from "../../../../utils/media";

const fallbackLocations = {
  tag: "MULTIPLE LOCATIONS",
  titleLine1: "We're located in 13+",
  titleLine2: "places in Iraq",
  description:
    "IRQGYP is a leading Iraqi company specialized in gypsum products and modern construction solutions — headquartered in Baghdad,",
  buttonText: "Get a free quote",
  buttonLink: "/contactus",
};

const tryParseJsonObject = (str) => {
  const t = String(str || "").trim();
  if (!t.startsWith("{") || !t.endsWith("}")) return null;
  try {
    const o = JSON.parse(t);
    return o && typeof o === "object" && !Array.isArray(o) ? o : null;
  } catch {
    return null;
  }
};

const coalesceLocationsPayload = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string") {
    return tryParseJsonObject(raw) || {};
  }
  if (typeof raw !== "object") return null;

  let base = { ...raw };
  if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    base = { ...raw, ...raw.data };
  }
  if (typeof raw.value === "string") {
    const parsed = tryParseJsonObject(raw.value);
    if (parsed) base = { ...base, ...parsed };
  } else if (raw.value && typeof raw.value === "object" && !Array.isArray(raw.value)) {
    base = { ...base, ...raw.value };
  }
  return base;
};

const pickString = (...candidates) => {
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "";
};

const getMapImageCandidates = (c) => {
  if (!c || typeof c !== "object") return [];
  return [
    c.mapImage,
    c.mapUrl,
    c.image,
    c.rightImage,
    c.heroImage,
    c.mediaUrl,
    c.MediaUrl,
    c.photo,
  ].filter((x) => x != null && x !== "");
};

const hasMeaningfulLocationsPayload = (c) => {
  if (!c || typeof c !== "object") return false;
  if (pickString(c.tag, c.sectionTag)) return true;
  if (pickString(c.titleLine1, c.titleLine2, c.title, c.heading)) return true;
  if (pickString(c.description, c.body, c.text, c.summary)) return true;
  if (pickString(c.buttonText, c.buttonLink, c.ctaText, c.ctaLink)) return true;
  return getMapImageCandidates(c).some((x) => Boolean(resolveMediaUrl(x)));
};

const Locations = ({ content }) => {
  const navigate = useNavigate();

  const view = useMemo(() => {
    const payload = coalesceLocationsPayload(content);
    if (!hasMeaningfulLocationsPayload(payload) || !payload) {
      return {
        tag: fallbackLocations.tag,
        titleLine1: fallbackLocations.titleLine1,
        titleLine2: fallbackLocations.titleLine2,
        description: fallbackLocations.description,
        buttonText: fallbackLocations.buttonText,
        buttonLink: fallbackLocations.buttonLink,
        mapSrc: mapImg,
      };
    }

    let titleLine1 = pickString(payload.titleLine1, payload.heading);
    let titleLine2 = pickString(payload.titleLine2, payload.subheading);
    const singleTitle = pickString(payload.title);
    if (!titleLine1 && singleTitle) {
      if (singleTitle.includes("\n")) {
        const parts = singleTitle.split("\n").map((p) => p.trim());
        titleLine1 = parts[0] || "";
        titleLine2 = parts.slice(1).join(" ").trim() || titleLine2;
      } else {
        titleLine1 = singleTitle;
      }
    }
    if (!titleLine1) titleLine1 = fallbackLocations.titleLine1;
    if (!titleLine2) {
      if (!(singleTitle && !singleTitle.includes("\n"))) {
        titleLine2 = fallbackLocations.titleLine2;
      }
    }

    let mapSrc = mapImg;
    for (const candidate of getMapImageCandidates(payload)) {
      const resolved = resolveMediaUrl(candidate);
      if (resolved) {
        mapSrc = resolved;
        break;
      }
    }

    return {
      tag: pickString(payload.tag, payload.sectionTag) || fallbackLocations.tag,
      titleLine1,
      titleLine2,
      description:
        pickString(payload.description, payload.body, payload.text, payload.summary, payload.content) ||
        fallbackLocations.description,
      buttonText: pickString(payload.buttonText, payload.ctaText, payload.buttonLabel) || fallbackLocations.buttonText,
      buttonLink: pickString(payload.buttonLink, payload.ctaLink, payload.link) || fallbackLocations.buttonLink,
      mapSrc,
    };
  }, [content]);

  return (
    <section className="locations">
      <div className="locations-container">
        <div className="locations-left">
          <span className="locations-tag tag">{view.tag}</span>

          <h2>
            {view.titleLine1}
            {view.titleLine2 ? (
              <>
                <br />
                <span>{view.titleLine2}</span>
              </>
            ) : null}
          </h2>

          <p>{view.description}</p>

          <button className="buttongyp" type="button" onClick={() => navigate(view.buttonLink)}>
            {view.buttonText}
          </button>
        </div>

        <div className="locations-right">
          <img
            src={view.mapSrc}
            alt=""
            onError={(e) => {
              if (e.currentTarget.src !== mapImg) {
                e.currentTarget.src = mapImg;
              }
            }}
          />
        </div>
      </div>
    </section>
  );
};

export default Locations;
